import { Injectable, inject } from '@angular/core';
import { ClockSyncService } from './clock-sync.service';
import { DatabaseService } from './database.service';
import { LoggerService } from './logger.service';

/**
 * Generic content-addressed binary blob storage.
 *
 * Any entity that stores a data: URL (a photo capture, an uploaded file, …)
 * in a synced table should not store the raw base64 inline — every sync
 * fetch would re-transfer it. Instead, extract it into the `blobs` table
 * (content-addressed by SHA-256 hash, so identical files are stored once)
 * and keep only a `blob:sha256:<hash>` reference string in the entity row.
 *
 * Typical usage in an entity service's save path:
 *   const stored = await this.blobStorage.extractBinaryToBlobs(rawFormData);
 *   // ...persist `stored` instead of `rawFormData`
 *
 * and on read:
 *   const resolved = await this.blobStorage.resolveBlobReferences(row.data);
 */
@Injectable({
  providedIn: 'root',
})
export class BlobStorageService {
  private db = inject(DatabaseService);
  private clockSync = inject(ClockSyncService);
  private logger = inject(LoggerService).getLogger('BlobStorageService');

  /**
   * Walk a plain object's top-level values (and arrays of values), replacing
   * any binary data: URL with a `blob:sha256:<hash>` reference into the
   * blobs table. Returns a new object — does not mutate the input.
   */
  async extractBinaryToBlobs(data: Record<string, any>): Promise<Record<string, any>> {
    const result = { ...data };

    for (const [key, value] of Object.entries(data)) {
      if (Array.isArray(value)) {
        result[key] = await Promise.all(
          value.map((v) => (this.isBinaryDataUrl(v) ? this.storeBlobAndGetReference(v) : v)),
        );
      } else if (this.isBinaryDataUrl(value)) {
        result[key] = await this.storeBlobAndGetReference(value);
      }
    }

    return result;
  }

  /**
   * Reverse of extractBinaryToBlobs — resolve `blob:sha256:<hash>` references
   * back to their original data: URLs. Returns a new object.
   */
  async resolveBlobReferences(data: Record<string, any>): Promise<Record<string, any>> {
    if (!data || typeof data !== 'object') {
      return {};
    }

    const result = { ...data };

    for (const [key, value] of Object.entries(data)) {
      if (Array.isArray(value)) {
        result[key] = await Promise.all(
          value.map((v) => (this.isBlobReference(v) ? this.loadBlob(v) : v)),
        );
      } else if (this.isBlobReference(value)) {
        result[key] = await this.loadBlob(value);
      }
    }

    return result;
  }

  /**
   * Check if a value is a binary data URL (candidate for blob extraction).
   */
  isBinaryDataUrl(value: any): boolean {
    if (typeof value !== 'string') return false;

    return (
      value.startsWith('data:image/') ||
      value.startsWith('data:application/pdf') ||
      value.startsWith('data:application/') // Other file types
    );
  }

  /**
   * Check if a value is a blob reference produced by extractBinaryToBlobs.
   */
  isBlobReference(value: any): boolean {
    return typeof value === 'string' && value.startsWith('blob:sha256:');
  }

  /**
   * Compute SHA-256 hash of a data: URL's payload.
   */
  private async computeHash(dataUrl: string): Promise<string> {
    // Extract base64 data (remove "data:image/jpeg;base64," prefix)
    const base64Data = dataUrl.split(',')[1];

    // Decode base64 to binary
    const binaryStr = atob(base64Data);
    const bytes = new Uint8Array(binaryStr.length);
    for (let i = 0; i < binaryStr.length; i++) {
      bytes[i] = binaryStr.charCodeAt(i);
    }

    // Compute SHA-256 hash
    const hashBuffer = await crypto.subtle.digest('SHA-256', bytes);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');

    return hashHex;
  }

  /**
   * Extract MIME type from a data: URL.
   */
  private getMimeType(dataUrl: string): string {
    const match = dataUrl.match(/^data:([^;]+)/);
    return match ? match[1] : 'application/octet-stream';
  }

  /**
   * Store a blob and return its reference string.
   *
   * Uses INSERT OR IGNORE to handle the race condition where two concurrent calls
   * with the same hash both pass the "does it exist?" check before either INSERT
   * completes. After the INSERT (or ignore), we always increment ref_count so the
   * count stays accurate regardless of which path was taken.
   */
  private async storeBlobAndGetReference(dataUrl: string): Promise<string> {
    const hash = await this.computeHash(dataUrl);

    // Extract data upfront (needed for INSERT)
    const base64Data = dataUrl.split(',')[1];
    const mimeType = this.getMimeType(dataUrl);
    const sizeBytes = Math.ceil(base64Data.length * 0.75); // Approximate binary size
    const now = this.clockSync.now();

    // INSERT OR IGNORE: silently skips if hash already exists (race condition or
    // legitimate dedup). ref_count starts at 0 so the UPDATE below always brings
    // it to the correct value whether this INSERT ran or was ignored.
    await this.db.run(
      `INSERT OR IGNORE INTO blobs (hash, data, mime_type, size_bytes, ref_count, created_at)
       VALUES (?, ?, ?, ?, 0, ?)`,
      [hash, base64Data, mimeType, sizeBytes, now],
    );

    // Always increment ref_count — covers both the new-insert and the reuse paths
    await this.db.run('UPDATE blobs SET ref_count = ref_count + 1 WHERE hash = ?', [hash]);

    this.logger.debug('Stored blob:', hash.substring(0, 8), mimeType, sizeBytes, 'bytes');
    return `blob:sha256:${hash}`;
  }

  /**
   * Load blob data by hash reference, returning it as a data: URL.
   */
  private async loadBlob(reference: string): Promise<string> {
    // Extract hash from "blob:sha256:abc123..."
    const hash = reference.replace('blob:sha256:', '');

    const results = await this.db.exec('SELECT data, mime_type FROM blobs WHERE hash = ?', [hash]);

    if (results.length === 0) {
      this.logger.error('Blob not found:', hash.substring(0, 8));
      return ''; // Return empty string if blob missing
    }

    const { data, mime_type } = results[0];
    return `data:${mime_type};base64,${data}`;
  }
}
